/* @flow */
/* eslint-env es6,browser */

import { setLoggerGif, setErrorExcludeReporting } from './utils/logger';
import { extend, isString, isArray, isFunction } from './utils/coreutils';

/**
 * @description Implements ad configuration functionality
 * @exports adconfig
 * @requires module:logger
 * @requires module:coreutils
 */
class _adconfig {
  _win: window;
  _doc: window.document;
  _pageInfo: pageInfo;
  _lastConfigLoadTime: number;
  _gPageInfo: pageInfo;
  _defConfig: adConfig;
  _config: adConfig;
  _configOverride: {};
  constructor() {
    this._win = window;
    this._doc = document;
    /**
     * @global
     * @typedef adConfig
     * @property {object} settings
     * @property {rotationConfig} settings.adRotate
     * @property {object.<string, hbPartnerConfig>} settings.headerBidding
     * @property {object.<string, posConfigInfo>} settings.posMap
     * @property {object.<string, perAdServerConfig>} settings.adServer
     * @property {string} settings.loggerGif
     * @property {number} settings.configExpirtySec
     * @property {number} settings.loadingDelay
     */
    this._defConfig = {
      settings: {
        adRotate: {},
        headerBidding: {},
        loggerGif: '//static.uolcontent.com/images/spacer.gif',
        errExcludeReporting: '',
        configExpirySec: 20 * 60,
        creatives: {},
        posMap: {},
        adServer: {},
        il: {
          obj: 'I12C',
          key: 'Morph'
        },
        adloading: {}
      }
    };
    this._config = this._config = extend(true, {}, this._defConfig);
    this._pageInfo = {
      posList: [],
      name: ''
    };
    this._lastConfigLoadTime = 0;
    this._gPageInfo = {
      posList: [],
      name: ''
    };
    this._configOverride = {};
  }

  /**
   * Initialize the object
   *
   * @typedef {object} globalParam
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {globalParam} g - global param object
   * @returns {undefined}
   */
  init(w: window, d: window.document, g: globalParam) {
    this._win = w;
    this._doc = d;
    this._configOverride = g.adConfig.override || {};
    this._gPageInfo = g.pageInfo;
    setLoggerGif(this._config.settings.loggerGif);
    setErrorExcludeReporting(this._config.settings.errExcludeReporting);
  }

  /**
   * Sets pageName & posList. posList is always cached as Array.
   *
   * @param {string} [pageName] - Pagename
   * @param {string|Array<string>} [posList] - Position list
   * @returns {undefined}
   */
  setPageInfo(pageName: ?string, posList: ?(string | Array<string>)) {
    this._pageInfo.name = pageName || '';
    posList = posList || '';
    if (isString(posList) && posList.length) {
      //$FlowFixMe
      this._pageInfo.posList = posList.split(',');
    } else if (isArray(posList) && posList.length) {
      this._pageInfo.posList = posList;
    }
  }

  /**
   * @global
   * @type function
   * @callback getPageNameCB
   * @param {object} context
   */
  /**
   * Getter function for pagename
   *
   * @param {string|getPageNameCB} s - input can be string or function
   * @returns {string} - Page name
   */
  getPageName(s: ?string | (window => string)): string {
    s = s || this._pageInfo.name || this._gPageInfo.name;
    if (isString(s)) {
      // $FlowFixMe
      return s;
    } else if (isFunction(s)) {
      // $FlowFixMe
      return s.call(this._win);
    }
    return '';
  }

  /**
   * @global
   * @type function
   * @callback getPagePositionsCB
   * @param {object} context
   * @param {string} page - pagename
   */
  /**
   * Getter function for ad positions for the current page
   *
   * @param {string|Array<string>|getPagePositionsCB} [v] - list of ad positions
   * @param {string|getPageNameCB} pageName - current page name
   * @param {boolean} [bRotation=false] - whether called during ad rotation
   * @returns {Array<string>} - Ad positions
   */
  getPagePositions(
    v: ?(string | Array<string> | ((window, string) => string)),
    pageName: ?(string | (window => string)),
    bRotation: ?boolean
  ): Array<string> {
    if (!v && this._pageInfo.posList.length) {
      v = this._pageInfo.posList;
    }
    if (!v) {
      v = this._gPageInfo.posList;
    }
    if (isFunction(v)) {
      // $FlowFixMe
      v = v.call(this._win, this.getPageName(pageName), bRotation === true);
    }
    //get and return array
    if (isString(v) && v.length) {
      // $FlowFixMe
      v = v.split(',');
    } else if (!isArray(v)) {
      v = [];
    }
    // $FlowFixMe
    return v;
  }

  /**
   * processConfigData - take cares of updating caches and propagating config change
   *
   * @param {adConfig} data - AdConfig data
   * @returns {undefined}
   * @access private
   */

  processConfigData(data: adConfig) {
    // update in-memory cache
    this._config = extend(
      true,
      {},
      this._defConfig,
      data,
      this._configOverride
    );

    if (this._config.settings.loggerGif) {
      setLoggerGif(this._config.settings.loggerGif);
    }
    setErrorExcludeReporting(this._config.settings.errExcludeReporting);
  }

  /**
   * getter for config
   *
   * @returns {adConfig} - Ad Config
   */
  get() {
    return this._config;
  }

  /**
   * getter for creatives
   *
   * @param {string} pos - Ad Position
   * @returns {string} - Creatives string
   */
  getCreative(pos: string = ''): string {
    let ret = '';
    if (!pos.length) {
      return ret;
    }
    const posInfo = this.getPosSettings(pos);
    const creativeKey = posInfo['creative'] || '';
    if (!creativeKey) {
      return ret;
    }

    const creatives = this._config.settings.creatives || {};
    ret = creatives[creativeKey] || '';
    return ret;
  }

  /**
   * @global
   * @type function
   * @callback contentHandlerCB
   * @param {string} page - pagename
   * @param {string} pos - ad position
   * @param {string} data - ad content
   */
  /**
   * @global
   * @typedef {object} posConfigInfo
   * @property {string} as
   * @property {boolean} [aFold=false]
   * @property {string} [auid]
   * @property {number} width
   * @property {number} height
   * @property {boolean} [iframe=true]
   * @property {boolean} [gscript=false]
   * @property {boolean} [dynHeight=false]
   * @property {number}	[MarginHeight]
   * @property {contentHandlerCB}	callback
   */
  /**
   * getter for position specific config
   *
   * @param {string} s - position name
   * @returns {posConfigInfo} - Position config
   */
  getPosSettings(s: string): posConfigInfo {
    //by default we will return 1*1 adSettings
    const _def: posConfigInfo = {
      width: 1,
      height: 1,
      as: '',
      loadingMethod: 1,
      aFold: false
    };
    let ret = {};
    if (s.length && this._config.settings.posMap[s]) {
      ret = this._config.settings.posMap[s];
    }
    ret = extend(true, _def, ret);
    return ret;
  }

  /**
   * getter for above the fold or not
   *
   * @param {string} pos - Ad Position
   * @returns {boolean} - is above fold?
   */
  isAboveFold(pos: string = ''): boolean {
    //By default all positions are below fold
    let aFold: boolean = false;
    if (!pos.length) {
      return aFold;
    }
    aFold = Boolean(this.getPosSettings(pos).aFold);
    return aFold;
  }
}

//export singleton object as default for this module
export default new _adconfig();
